import { REG_FORM_CONTROL_TYPE_ENUM } from '../../config/constants';
import { debug } from '../logger';
import { getFromStorage, saveToStorage } from '../storageHelper';

const formatInputAnswers = (answers) =>
  answers
    ?.filter(
      (a) =>
        (a.value || a.file) &&
        (a.fkRegFormControlType !== REG_FORM_CONTROL_TYPE_ENUM.CHECKBOX ||
          a.value?.toLowerCase() === 'yes')
    )
    .map(
      ({
        fkEcomVendorProductInput,
        previewImage,
        value,
        fkRegFormControlType
      }) => ({
        fkEcomVendorProductInput,
        previewImage,
        value:
          fkRegFormControlType === REG_FORM_CONTROL_TYPE_ENUM.CHECKBOX
            ? 'yes'
            : value
      })
    ) || [];

const matchInputAnswers = (pAnswers, cAnswers) => {
  const pAnswersFormatted = formatInputAnswers(pAnswers);
  const cAnswersFormatted = formatInputAnswers(cAnswers);
  return (
    cAnswersFormatted?.length === pAnswersFormatted.length &&
    pAnswersFormatted.every((a) =>
      cAnswersFormatted.some(
        (e) =>
          e.fkEcomVendorProductInput === a.fkEcomVendorProductInput &&
          (!e.value && !!e.previewImage
            ? a.previewImage === e.previewImage
            : e.value?.trim() === a.value?.trim())
      )
    )
  );
};

const useCartStorageController = ({ isAdminCheckout = false } = {}) => {
  const defaultCartState = {
    secureId: null,
    email: null,
    regCartProducts: [],
    ecomStoreCartProducts: [],
    ecomStoreCartPackages: [],
    shippingOptions: [],
    shippingAddress: null
  };

  const getCartStorageId = (identifier) =>
    isAdminCheckout
      ? `user-cart-${identifier}-admin`
      : `user-cart-${identifier}`;

  const resetCart = (identifier) => {
    const cart = { ...defaultCartState };
    saveToStorage(
      getCartStorageId(identifier, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const getGuestCartStorage = (identifier) => {
    const idString = getCartStorageId(identifier, isAdminCheckout);
    const cartString = getFromStorage(idString);
    if (cartString) {
      try {
        const cart = JSON.parse(cartString);
        cart.regCartProducts = cart.regCartProducts || [];
        cart.ecomStoreCartProducts = cart.ecomStoreCartProducts || [];
        cart.ecomStoreCartPackages = cart.ecomStoreCartPackages || [];
        cart.shippingOptions = cart.shippingOptions || [];

        return cart;
      } catch (e) {
        debug(e);
      }
    }

    return resetCart(identifier);
  };

  const setGuestCartProductsAndPackages = (
    identifier,
    { ecomStoreCartProducts, ecomStoreCartPackages }
  ) => {
    const cart = getGuestCartStorage(identifier, isAdminCheckout);
    cart.ecomStoreCartProducts = ecomStoreCartProducts;
    cart.ecomStoreCartPackages = ecomStoreCartPackages;

    saveToStorage(
      getCartStorageId(identifier, isAdminCheckout),
      JSON.stringify(cart)
    );

    return cart;
  };

  const setCartEmail = (identifier, email) => {
    const cart = getGuestCartStorage(identifier, isAdminCheckout);
    cart.email = email;
    saveToStorage(
      getCartStorageId(identifier, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const setCartFundraiser = (
    identifier,
    { fkEcomStoreRosterPlayer, customInput }
  ) => {
    const cart = getGuestCartStorage(identifier, isAdminCheckout);
    cart.fundraiser = { fkEcomStoreRosterPlayer, customInput };
    saveToStorage(
      getCartStorageId(identifier, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const setCartSecureId = (identifier, secureId) => {
    const cart = getGuestCartStorage(identifier, isAdminCheckout);
    cart.secureId = secureId;
    saveToStorage(
      getCartStorageId(identifier, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const setCartShippingAddress = (identifier, ecomCustomerAddress) => {
    const cart = getGuestCartStorage(identifier, isAdminCheckout);
    cart.shippingAddress = ecomCustomerAddress;
    saveToStorage(
      getCartStorageId(identifier, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const setCartShippingOptions = (identifier, shippingOptions) => {
    const cart = getGuestCartStorage(identifier, isAdminCheckout);
    cart.shippingOptions = shippingOptions;
    saveToStorage(
      getCartStorageId(identifier, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const updateGuestCartProduct = (
    cartProduct,
    productRequests = [],
    appendQuantity = false
  ) => {
    const productRequest = productRequests.find(
      (c) =>
        (c.uuid === cartProduct.uuid ||
          c.pkEcomStoreCartProduct === cartProduct.pkEcomStoreCartProduct) &&
        matchInputAnswers(
          cartProduct.ecomStoreCartProductInputAnswers,
          c.ecomStoreCartProductInputAnswers
        )
    );

    if (!productRequest) {
      return cartProduct;
    }

    return {
      ...cartProduct,
      count: appendQuantity
        ? cartProduct.count + productRequest.count
        : productRequest.count,
      ecomStoreCartProductInputAnswers: formatInputAnswers(
        productRequest.ecomStoreCartProductInputAnswers || []
      )
    };
  };

  const addOrUpdateGuestCartProducts = (
    guestCartProducts,
    productRequests = []
  ) => {
    const updatedCartProducts = guestCartProducts.map((cartProduct) =>
      updateGuestCartProduct(cartProduct, productRequests, true)
    );

    const newCartProducts =
      productRequests
        ?.filter(
          (c) =>
            !guestCartProducts.some(
              (p) =>
                c.uuid === p.uuid &&
                matchInputAnswers(
                  p.ecomStoreCartProductInputAnswers,
                  c.ecomStoreCartProductInputAnswers
                )
            )
        )
        .map((c, index) => ({
          pkEcomStoreCartProduct: Date.now() + index,
          uuid: c.uuid,
          count: c.count,
          ecomStoreCartProductInputAnswers:
            formatInputAnswers(c.ecomStoreCartProductInputAnswers) || [],
          addToGuestCart: true
        })) ?? [];

    return [...updatedCartProducts, ...newCartProducts];
  };

  const updateGuestCartPackage = (cartProduct, packageRequests = []) => {
    const packageRequest = packageRequests.find(
      (c) => c.pkEcomStoreCartPackage === cartProduct.pkEcomStoreCartPackage
    );

    if (!packageRequest) {
      return cartProduct;
    }

    const products = addOrUpdateGuestCartProducts(
      [],
      packageRequest.ecomStoreCartProducts
    );

    return {
      ...cartProduct,
      count: packageRequest.count,
      ecomStoreCartProducts:
        products.length > 0 ? products : cartProduct.ecomStoreCartProducts
    };
  };

  const addOrUpdateGuestCartPackages = (
    guestCartPackages,
    packageRequests = []
  ) => {
    const updatedCartPackages = guestCartPackages.map((cartPackage) =>
      updateGuestCartPackage(cartPackage, packageRequests)
    );

    const newCartPackages =
      packageRequests
        ?.filter((c) => !c.pkEcomStoreCartPackage)
        .map((c, index) => ({
          ...c,
          pkEcomStoreCartPackage: Date.now() + index,
          ecomStoreCartProducts: addOrUpdateGuestCartProducts(
            [],
            c.ecomStoreCartProducts
          ),
          addToGuestCart: true
        })) ?? [];

    return [...updatedCartPackages, ...newCartPackages];
  };

  const addGuestCartProductsAndPackages = (
    guestCart,
    { ecomStoreCartProducts, ecomStoreCartPackages }
  ) => {
    const updatedCartProducts = addOrUpdateGuestCartProducts(
      guestCart.ecomStoreCartProducts,
      ecomStoreCartProducts
    );

    const updatedCartPackages = addOrUpdateGuestCartPackages(
      guestCart.ecomStoreCartPackages,
      ecomStoreCartPackages
    );

    return {
      ecomStoreCartProducts: updatedCartProducts,
      ecomStoreCartPackages: updatedCartPackages
    };
  };

  const updateGuestCartProductsAndPackages = (
    guestCart,
    { ecomStoreCartProducts, ecomStoreCartPackages }
  ) => {
    const updatedCartProducts = guestCart.ecomStoreCartProducts.map(
      (cartProduct) =>
        updateGuestCartProduct(cartProduct, ecomStoreCartProducts)
    );
    const updatedCartPackages = guestCart.ecomStoreCartPackages.map(
      (cartPackage) =>
        updateGuestCartPackage(cartPackage, ecomStoreCartPackages)
    );

    return {
      ecomStoreCartProducts: updatedCartProducts,
      ecomStoreCartPackages: updatedCartPackages
    };
  };

  const onFetchGuestCartCallback = (
    pkEcomStore,
    data,
    { ecomStoreCartProducts, ecomStoreCartPackages }
  ) => {
    const filteredProducts = ecomStoreCartProducts
      .filter((requestProduct) =>
        data.ecomStoreCartProducts.some(
          (cartProduct) =>
            requestProduct.pkEcomStoreCartProduct ===
            cartProduct.pkEcomStoreCartProduct
        )
      )
      .map((p) => {
        const cartProduct = data.ecomStoreCartProducts.find(
          (c) => c.pkEcomStoreCartProduct === p.pkEcomStoreCartProduct
        );
        return {
          ...p,
          addToGuestCart: false,
          count: cartProduct.count
        };
      });

    const filteredPackages = ecomStoreCartPackages
      .filter((requestPackage) =>
        data.ecomStoreCartPackages.some(
          (cartPackage) =>
            requestPackage.pkEcomStoreCartPackage ===
            cartPackage.pkEcomStoreCartPackage
        )
      )
      .map((p) => {
        const cartPackage = data.ecomStoreCartPackages.find(
          (c) => c.pkEcomStoreCartPackage === p.pkEcomStoreCartPackage
        );
        return {
          ...p,
          addToGuestCart: false,
          count: cartPackage.count
        };
      });

    setGuestCartProductsAndPackages(pkEcomStore, {
      ecomStoreCartProducts: filteredProducts,
      ecomStoreCartPackages: filteredPackages
    });
  };

  return {
    getGuestCartStorage,
    setCartEmail,
    setCartSecureId,
    setCartShippingAddress,
    setCartShippingOptions,
    setCartFundraiser,
    resetCart,
    formatInputAnswers,
    matchInputAnswers,
    addGuestCartProductsAndPackages,
    updateGuestCartProductsAndPackages,
    onFetchGuestCartCallback
  };
};

export { useCartStorageController, formatInputAnswers, matchInputAnswers };
